// ============================================================
// ALL IS WELL — DIAGNOSTICS DATA
// Only verified diagnostic services. Never invent availability.
// Use COMING SOON for unverified data.
// ============================================================

export interface DiagnosticCategory {
  id: string;
  label: string;
  description: string;
  icon: string; // Lucide icon name
  color: string;
}

export interface DiagnosticService {
  id: string;
  name: string;
  categoryId: string;
  description?: string;
  availableCentreIds: string[]; // Only verified centres
  preparationNotes?: string; // e.g. "Fasting required"
  reportTime?: string; // e.g. "Same day" — only if verified
}

// ============================================================
// DIAGNOSTIC CATEGORIES — Patient-friendly navigation
// ============================================================
export const diagnosticCategories: DiagnosticCategory[] = [
  {
    id: 'blood-tests',
    label: 'BLOOD TESTS',
    description: 'CBC, lipid profile, HbA1c, thyroid, liver function and more',
    icon: 'Droplets',
    color: 'rose',
  },
  {
    id: 'imaging',
    label: 'IMAGING',
    description: 'X-Ray, Ultrasound, CT Scan, MRI',
    icon: 'ScanLine',
    color: 'blue',
  },
  {
    id: 'heart-tests',
    label: 'HEART TESTS',
    description: 'ECG, Echo, Stress Test, Holter monitoring',
    icon: 'HeartPulse',
    color: 'red',
  },
  {
    id: 'neuro-tests',
    label: 'NEURO TESTS',
    description: 'EEG, nerve conduction studies, brain imaging',
    icon: 'Brain',
    color: 'violet',
  },
  {
    id: 'respiratory-tests',
    label: 'RESPIRATORY TESTS',
    description: 'Spirometry, pulmonary function tests',
    icon: 'Wind',
    color: 'sky',
  },
  {
    id: 'hearing',
    label: 'HEARING',
    description: 'Audiometry, hearing assessment',
    icon: 'Ear',
    color: 'amber',
  },
  {
    id: 'bone-joint',
    label: 'BONE & JOINT TESTS',
    description: 'Bone density, joint imaging, DEXA scan',
    icon: 'Bone',
    color: 'slate',
  },
  {
    id: 'womens-health',
    label: "WOMEN'S HEALTH",
    description: 'Mammography, Pap smear, hormonal tests, obstetric scans',
    icon: 'HeartHandshake',
    color: 'pink',
  },
  {
    id: 'eye-vision',
    label: 'EYE / VISION',
    description: 'Visual acuity, retinal examination, IOP',
    icon: 'Eye',
    color: 'teal',
  },
  {
    id: 'pathology',
    label: 'PATHOLOGY',
    description: 'Biopsy, histopathology, cytology',
    icon: 'Microscope',
    color: 'purple',
  },
  {
    id: 'other-tests',
    label: 'OTHER TESTS',
    description: 'Urine analysis, stool tests, culture & sensitivity',
    icon: 'FlaskConical',
    color: 'green',
  },
];

// ============================================================
// DIAGNOSTIC SERVICES — Prototype data
// IMPORTANT: These are representative services only.
// Actual availability must be verified with All Is Well.
// ============================================================
export const diagnosticServices: DiagnosticService[] = [
  // Blood Tests
  {
    id: 'cbc',
    name: 'Complete Blood Count (CBC)',
    categoryId: 'blood-tests',
    description: 'Measures red blood cells, white blood cells, and platelets',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic', 'sanawad-super-clinic'],
    preparationNotes: 'No special preparation required',
  },
  {
    id: 'lipid-profile',
    name: 'Lipid Profile',
    categoryId: 'blood-tests',
    description: 'Cholesterol, triglycerides, HDL, LDL levels',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
    preparationNotes: '12-hour fasting required',
  },
  {
    id: 'hba1c',
    name: 'HbA1c (Glycated Haemoglobin)',
    categoryId: 'blood-tests',
    description: 'Average blood sugar over the past 3 months',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
    preparationNotes: 'No fasting required',
  },
  {
    id: 'thyroid-profile',
    name: 'Thyroid Profile (T3, T4, TSH)',
    categoryId: 'blood-tests',
    description: 'Thyroid hormone levels',
    availableCentreIds: ['burhanpur-super-clinic'],
    preparationNotes: 'Morning sample preferred',
  },
  {
    id: 'liver-function',
    name: 'Liver Function Test (LFT)',
    categoryId: 'blood-tests',
    description: 'Liver enzymes and function markers',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
    preparationNotes: '8-hour fasting required',
  },
  {
    id: 'kidney-function',
    name: 'Kidney Function Test (KFT)',
    categoryId: 'blood-tests',
    description: 'Creatinine, urea, electrolytes',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
  },
  // Imaging
  {
    id: 'xray',
    name: 'X-Ray',
    categoryId: 'imaging',
    description: 'Chest, spine, limb and other X-ray imaging',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
  },
  {
    id: 'ultrasound',
    name: 'Ultrasound',
    categoryId: 'imaging',
    description: 'Abdominal, pelvic, obstetric and other ultrasound',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
  },
  {
    id: 'ct-scan',
    name: 'CT Scan',
    categoryId: 'imaging',
    description: 'Computed tomography for detailed cross-sectional imaging',
    availableCentreIds: ['burhanpur-super-clinic'],
  },
  {
    id: 'mri',
    name: 'MRI',
    categoryId: 'imaging',
    description: 'Magnetic resonance imaging for soft tissue evaluation',
    availableCentreIds: ['burhanpur-super-clinic'],
  },
  // Heart Tests
  {
    id: 'ecg',
    name: 'ECG (Electrocardiogram)',
    categoryId: 'heart-tests',
    description: 'Records the electrical activity of the heart',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic'],
    preparationNotes: 'No special preparation required',
  },
  {
    id: 'echo',
    name: 'Echocardiogram (Echo)',
    categoryId: 'heart-tests',
    description: 'Ultrasound imaging of the heart',
    availableCentreIds: ['burhanpur-super-clinic'],
  },
  // Bone & Joint
  {
    id: 'dexa-scan',
    name: 'DEXA Scan (Bone Density)',
    categoryId: 'bone-joint',
    description: 'Measures bone mineral density',
    availableCentreIds: ['burhanpur-super-clinic'],
  },
  // Women's Health
  {
    id: 'obstetric-scan',
    name: 'Obstetric Ultrasound',
    categoryId: 'womens-health',
    description: 'Pregnancy monitoring and foetal assessment',
    availableCentreIds: ['burhanpur-super-clinic', 'khandwa-super-clinic', 'dharni-super-clinic'],
  },
  // Pathology
  {
    id: 'blood-bank-services',
    name: 'Blood Bank Services',
    categoryId: 'pathology',
    description: 'Blood grouping, cross-matching, transfusion services',
    availableCentreIds: ['burhanpur-super-clinic', 'phopnar-collection-center', 'sanawad-super-clinic'],
  },
];

// ============================================================
// SEARCH — simple keyword matching
// ============================================================
export const searchDiagnostics = (query: string): DiagnosticService[] => {
  if (!query.trim()) return diagnosticServices;
  const q = query.toLowerCase();
  return diagnosticServices.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.description?.toLowerCase().includes(q) ||
      s.categoryId.toLowerCase().includes(q)
  );
};

export const getDiagnosticsByCategory = (categoryId: string): DiagnosticService[] =>
  diagnosticServices.filter((s) => s.categoryId === categoryId);

export const getDiagnosticCategoryById = (id: string): DiagnosticCategory | undefined =>
  diagnosticCategories.find((c) => c.id === id);
